import { captureCaughtException } from "@/lib/analytics";
import { isFetchError, type FetchError } from "@repo/api-sdk";

/** What a salon sees when we have nothing more specific to say. */
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

/**
 * The fallback for a toast whose title already names the failed action, so the
 * description only has to say what to do next.
 */
export const RETRY_HINT = "Please try again.";

/**
 * The statuses on which the API deliberately explains itself to the person who
 * made the request: 400 (invalid input, e.g. a field-level validation message),
 * 409 (a conflict, e.g. "email already in use") and 422 (the action is refused
 * for a stated business reason).
 *
 * Everything else - 401/403/404, 429, any 5xx, a dropped connection, a thrown
 * non-HTTP error - is not feedback for the user. Its message is whatever the
 * server or the runtime happened to say ("boom", a stack-flavoured string, an id
 * that was not found), which means nothing to a salon and can leak internals.
 */
const USER_FACING_STATUSES: ReadonlySet<number> = new Set([400, 409, 422]);

interface MessageOptions {
  /**
   * For surfaces whose whole job is to relay an auth or link check (sign-in, a
   * password reset, a concierge or confirmation link): there a 401/403/404 is
   * the answer ("invalid email or password", "this link has expired"), so any
   * 4xx except 429 is shown. A 5xx or a network failure never is.
   */
  trustClientErrors?: boolean;
}

/**
 * Whether `error` is one the API meant the user to read: an HTTP error with a
 * message and one of {@link USER_FACING_STATUSES} (or, with
 * `trustClientErrors`, any 4xx but 429).
 */
function isUserFacingError(
  error: unknown,
  options: MessageOptions = {}
): error is FetchError {
  if (!isFetchError(error) || !error.message) return false;
  // `status` is optional on the SDK's FetchError (no response, no status).
  const status = error.status ?? 0;
  if (USER_FACING_STATUSES.has(status)) return true;
  return (
    options.trustClientErrors === true &&
    status >= 400 &&
    status < 500 &&
    status !== 429
  );
}

/**
 * The text to show the user for a failed request: the server's own message for a
 * deliberate user-facing error, otherwise `fallback`. Pure - it neither reports
 * nor logs, so it is safe to call while rendering.
 */
function userFacingErrorMessage(
  error: unknown,
  fallback: string = GENERIC_ERROR_MESSAGE,
  options: MessageOptions = {}
): string {
  return isUserFacingError(error, options) ? error.message : fallback;
}

/**
 * Report an error the user will not be shown, once per call. Expected,
 * user-facing errors (a validation message the API chose to send) and routine
 * 401/429 responses are not reported: they are the product working, not a fault.
 *
 * Goes through `captureCaughtException`, so PostHog and Sentry both get it and
 * the operator blackout applies.
 */
export function reportUnexpectedError(
  error: unknown,
  boundary: string,
  options: MessageOptions = {}
): void {
  if (isUserFacingError(error, options) || isRoutineFailure(error)) return;
  captureCaughtException(error, { boundary });
}

/**
 * Failures that say nothing about the app's health: an expired or missing
 * session (401) and being rate limited (429). The user still gets the friendly
 * copy; there is just nothing to investigate, and reporting every expired tab
 * would bury real faults.
 */
function isRoutineFailure(error: unknown): boolean {
  if (!isFetchError(error)) return false;
  return error.status === 401 || error.status === 429;
}

/**
 * For a `catch` or `onError` handler: report the failure if it is unexpected and
 * return the text to show. Call it once per failure, never while rendering - use
 * `useReportLoadFailure` for an error that is read from a query during render.
 */
export function handleActionError(
  error: unknown,
  options: MessageOptions & { boundary: string; fallback?: string }
): string {
  reportUnexpectedError(error, options.boundary, options);
  return userFacingErrorMessage(error, options.fallback, options);
}
